import {
  type I18nLocale,
  type I18nMessage,
  toI18nMessage,
  translate,
} from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import { parseDate } from './dateOperations';
import { formatDateMedium } from './dateTimeUtils';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

export type Recency =
  | { unit: 'justNow' }
  | { unit: 'minutesAgo' | 'hoursAgo' | 'daysAgo'; count: number }
  | { unit: 'date' };

export type RelativeRecency = Exclude<Recency, { unit: 'date' }>;

export const toRecency = (elapsedMs: number): Recency => {
  if (elapsedMs >= WEEK_MS) return { unit: 'date' };
  if (elapsedMs < MINUTE_MS) return { unit: 'justNow' };
  const minutes = Math.round(elapsedMs / MINUTE_MS);
  if (minutes < 60) return { unit: 'minutesAgo', count: minutes };
  const hours = Math.round(elapsedMs / HOUR_MS);
  if (hours < 24) return { unit: 'hoursAgo', count: hours };
  return { unit: 'daysAgo', count: Math.round(elapsedMs / DAY_MS) };
};

export const toRecencyMessage = (recency: RelativeRecency): I18nMessage => {
  if (recency.unit === 'justNow') {
    return toI18nMessage('dateTime:justNow', 'Just now');
  }
  const { unit, count } = recency;
  switch (unit) {
    case 'minutesAgo':
      return toI18nMessage(
        'dateTime:minutesAgo',
        count === 1 ? '{{count}} minute ago' : '{{count}} minutes ago',
        { count }
      );
    case 'hoursAgo':
      return toI18nMessage(
        'dateTime:hoursAgo',
        count === 1 ? '{{count}} hour ago' : '{{count}} hours ago',
        { count }
      );
    case 'daysAgo':
      return toI18nMessage(
        'dateTime:daysAgo',
        count === 1 ? '{{count}} day ago' : '{{count}} days ago',
        { count }
      );
  }
};

export type FormatRecencyArgs = {
  now: Date;
  locale: Locale;
  i18nLocale: I18nLocale;
};

export const formatRecency = (
  timestamp: string,
  { now, locale, i18nLocale }: FormatRecencyArgs
): string => {
  const recency = toRecency(now.getTime() - parseDate(timestamp).getTime());
  if (recency.unit === 'date') return formatDateMedium(timestamp, locale);
  return translate(i18nLocale, toRecencyMessage(recency));
};
